import { format, isValid, parse } from 'date-fns';
import type { Project } from '@/api/client';
import { formatDecimal } from '@/utils/formatNumber';
import { getProjectTypeText } from '@/utils/projectTypeColors';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import { uniqueFirstByTrimmedName } from '@/utils/uniqueByTrimmedName';
import { formatNoEdits, getReviewCheckTypeLabel } from '@/utils/reviewCheckType';
import { highlightPlainText } from '@/utils/highlightSearchTerms';
import { ReviewComboCell } from './ReviewComboCell';

/** Поля блока «Проверка» — для любого комбо только из них показываем все строки, включая N/A (как пресет review_combo) */
const REVIEW_COMBO_FIELD_IDS = new Set(['review_deadline', 'review_check_type', 'corrections_number', 'no_edits']);

function isReviewFieldsOnlyCombo(columnIds: string[]): boolean {
  return columnIds.length > 0 && columnIds.every((id) => REVIEW_COMBO_FIELD_IDS.has(id));
}

function formatDateRu(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const s = value.trim();
  const d = parse(s.slice(0, 10), 'yyyy-MM-dd', new Date());
  if (isValid(d)) return format(d, 'dd.MM.yyyy');
  const d2 = new Date(s);
  return !isNaN(d2.getTime()) ? format(d2, 'dd.MM.yyyy') : s;
}

interface ComboCellProps {
  project: Project;
  columnIds: string[];
  labels: Record<string, string>;
  searchHighlightWords?: string[];
}

type CellValue = string | string[] | null;

function getValue(project: Project, columnId: string): CellValue {
  switch (columnId) {
    case 'asta':
      return project.asta?.trim() || null;
    case 'ase':
      return project.ase?.trim() || null;
    case 'rin':
      return project.rin?.trim() || null;
    case 'producer':
      return (project.producers ?? []).map((p) => p.name).join(', ') || null;
    case 'techeditor':
      return (project.techeditors ?? []).map((t) => t.name).join(', ') || null;
    case 'corrector':
      return (project.correctors ?? []).map((c) => c.name).join(', ') || null;
    case 'studio': {
      const contractors = project.project_contracts ?? project.contractors ?? [];
      if (!contractors.length) return null;
      const names = uniqueFirstByTrimmedName(contractors)
        .map((c) => contractorDisplayName(c))
        .filter(Boolean);
      return names.length ? names.join(', ') : null;
    }
    case 'department':
      return project.edition_department_code?.trim() || null;
    case 'edition':
      return project.edition_name?.trim() || null;
    case 'editor':
      return project.editor_name?.trim() || null;
    case 'author':
      return (project.authors ?? []).map((a) => a.name).join(', ') || null;
    case 'readers': {
      const readers = project.readers ?? [];
      if (!readers.length) return null;
      const names = uniqueFirstByTrimmedName(readers)
        .map((r) => (r.name ?? '').trim())
        .filter(Boolean);
      return names.length ? names.join(', ') : null;
    }
    case 'name':
      return project.name?.trim() || null;
    case 'pages':
      return project.pages != null ? formatDecimal(project.pages) : null;
    case 'author_sheets':
      return project.author_sheets != null ? formatDecimal(project.author_sheets) : null;
    case 'tracks_count':
      return project.tracks_count != null ? String(project.tracks_count) : null;
    case 'size':
      return project.size != null ? formatDecimal(project.size) : null;
    case 'deadline':
      return formatDateRu(project.deadline);
    case 'review_check_type':
      return getReviewCheckTypeLabel(project.review_check_type);
    case 'review_deadline':
      return formatDateRu(project.review_deadline);
    case 'corrections_number':
      return project.corrections_number != null ? String(project.corrections_number) : null;
    case 'no_edits':
      return formatNoEdits(project.no_edits);
    case 'time':
      return project.time?.trim() || null;
    case 'recording_coef':
      return project.recording_coef != null && Number.isFinite(Number(project.recording_coef))
        ? formatDecimal(project.recording_coef)
        : null;
    case 'montage_coef':
      return project.montage_coef != null && Number.isFinite(Number(project.montage_coef))
        ? formatDecimal(project.montage_coef)
        : null;
    case 'rights':
      return project.rights?.trim() || null;
    case 'project_type':
      return getProjectTypeText(project.project_type) || null;
    case 'theme':
      return project.theme?.trim() || null;
    case 'paper_book_plan':
      return formatDateRu(project.paper_book_plan);
    default:
      return null;
  }
}

function hasContent(value: CellValue): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value.length > 0;
}

export function ComboCell({ project, columnIds, labels, searchHighlightWords }: ComboCellProps) {
  if (isReviewFieldsOnlyCombo(columnIds)) {
    return (
      <ReviewComboCell project={project} columnIds={columnIds} searchHighlightWords={searchHighlightWords} />
    );
  }

  const hl = (text: string) =>
    searchHighlightWords?.length ? highlightPlainText(text, searchHighlightWords) : text;

  const items = columnIds.map((id) => {
    const label = labels[id] ?? id;
    const value = getValue(project, id);
    return { id, label, value };
  });
  const hasAnyContent = items.some((i) => hasContent(i.value));

  if (!hasAnyContent) {
    return (
      <div className="projects-table__combo-cell projects-table__combo-cell_empty">
        <span className="projects-table__empty-value">{hl('N/A')}</span>
      </div>
    );
  }

  return (
    <div className="projects-table__combo-cell projects-table__labeled-cell projects-table__labeled-cell_has-content">
      {items.flatMap(({ id, label, value }) => {
        if (value == null) return [];
        const values = Array.isArray(value) ? value : [value];
        return values.map((v, idx) => (
          <span
            key={`${id}-${idx}`}
            className={
              id === 'edition'
                ? 'projects-table__combo-line projects-table__combo-line_edition'
                : id === 'review_check_type'
                  ? 'projects-table__combo-line projects-table__combo-line_review-check-type'
                    : id === 'editor'
                    ? 'projects-table__combo-line projects-table__combo-line_editor'
                    : id === 'techeditor' || id === 'corrector'
                      ? 'projects-table__combo-line projects-table__combo-line_techeditor'
                      : undefined
            }
          >
            <span className="projects-table__labeled-cell-label">{hl(`${label}:`)}</span> {hl(v)}
          </span>
        ));
      })}
    </div>
  );
}

ComboCell.displayName = 'ComboCell';
